import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

const PLUGIN_ID = 'panda';
const BOARDS_PATH = '/jira/boards';
const PROJECTS_PATH = '/jira/projects';
const PROJECT_KEY_PARAM = 'projectKey';
const QUERY_PARAM = 'query';
const PROJECT_UNKNOWN = 'jira_project_unknown';
const MAX_DETAIL_LENGTH = 200;
const NO_DETAIL = 'the request did not complete';

export type JiraBoardFilter = {
  id: string;
  equivalentBoardIds: number[];
};

export type JiraBoard = {
  id: number;
  name: string;
  type: string;
  filter?: JiraBoardFilter;
};

export type JiraProject = {
  key: string;
  name: string;
};

export type JiraBoardsOutcome =
  | { status: 'listed'; boards: JiraBoard[] }
  | { status: 'projectUnknown' }
  | { status: 'unavailable'; detail: string };

export type JiraProjectsOutcome =
  | { status: 'listed'; projects: JiraProject[]; isLast: boolean }
  | { status: 'unavailable'; detail: string };

export type JiraListings = {
  boardsOf: (projectKey: string) => Promise<JiraBoardsOutcome>;
  searchProjects: (query: string) => Promise<JiraProjectsOutcome>;
};

const bodyOf = async (response: Response): Promise<Record<string, unknown>> => {
  try {
    const parsed = await response.json();
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
};

const detailOf = (body: Record<string, unknown>, status: number): string =>
  typeof body.detail === 'string' && body.detail.length > 0
    ? body.detail.slice(0, MAX_DETAIL_LENGTH)
    : `HTTP ${status}`;

const detailOfFailure = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error);
  return message.slice(0, MAX_DETAIL_LENGTH) || NO_DETAIL;
};

type Read =
  | { ok: true; body: Record<string, unknown> }
  | { ok: false; body: Record<string, unknown>; detail: string };

export const jiraListings = (
  discovery: DiscoveryApi,
  fetchApi: FetchApi,
): JiraListings => {
  const read = async (
    path: string,
    params: Record<string, string>,
  ): Promise<Read> => {
    try {
      const base = await discovery.getBaseUrl(PLUGIN_ID);
      const query = new URLSearchParams(params).toString();
      const response = await fetchApi.fetch(`${base}${path}?${query}`);
      const body = await bodyOf(response);
      return response.ok
        ? { ok: true, body }
        : { ok: false, body, detail: detailOf(body, response.status) };
    } catch (error) {
      return { ok: false, body: {}, detail: detailOfFailure(error) };
    }
  };

  return {
    boardsOf: async projectKey => {
      const answer = await read(BOARDS_PATH, {
        [PROJECT_KEY_PARAM]: projectKey,
      });
      if (!answer.ok) {
        return answer.body.error === PROJECT_UNKNOWN
          ? { status: 'projectUnknown' }
          : { status: 'unavailable', detail: answer.detail };
      }
      return {
        status: 'listed',
        boards: Array.isArray(answer.body.boards)
          ? (answer.body.boards as JiraBoard[])
          : [],
      };
    },

    searchProjects: async query => {
      const answer = await read(PROJECTS_PATH, { [QUERY_PARAM]: query });
      if (!answer.ok) {
        return { status: 'unavailable', detail: answer.detail };
      }
      return {
        status: 'listed',
        projects: Array.isArray(answer.body.projects)
          ? (answer.body.projects as JiraProject[])
          : [],
        isLast: answer.body.isLast === true,
      };
    },
  };
};
